import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { featuredComics } from '@/mocks/comics';

export default function ComicDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [activeTab, setActiveTab] = useState<'chapters' | 'comments'>('chapters');

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [id]);

  const comic = featuredComics.find((c) => c.id === Number(id));

  if (!comic) {
    return (
      <>
        <Navbar />
        <main className="min-h-screen bg-background-50 pt-24 flex items-center justify-center">
          <div className="text-center">
            <span className="w-20 h-20 flex items-center justify-center mx-auto mb-4 text-foreground-300">
              <i className="ri-file-unknow-line text-5xl"></i>
            </span>
            <h2 className="font-heading text-2xl font-bold text-foreground-950 mb-2">漫画未找到</h2>
            <p className="text-foreground-500 text-sm mb-6">抱歉，您要找的漫画不存在或已下架</p>
            <Link
              to="/category"
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              浏览其他漫画
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-arrow-right-line text-sm"></i>
              </span>
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const pageTitle = `${comic.title} - 尾巴漫画网`;
  const pageDesc = `${comic.title}，${comic.author}作品，${comic.category}韩漫，评分${comic.rating}，${comic.status}，共${comic.chapters}话。${comic.description}`;

  return (
    <>
      <Navbar />
      <main>
        {/* Breadcrumb */}
        <div className="bg-background-50 pt-24 md:pt-32 pb-0">
          <div className="w-full px-4 md:px-6">
            <nav className="flex items-center gap-2 text-xs text-foreground-500 mb-2 flex-wrap">
              <Link to="/" className="hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap">
                首页
              </Link>
              <span className="w-3 h-3 flex items-center justify-center">
                <i className="ri-arrow-right-s-line text-xs"></i>
              </span>
              <Link to="/category" className="hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap">
                分类大全
              </Link>
              <span className="w-3 h-3 flex items-center justify-center">
                <i className="ri-arrow-right-s-line text-xs"></i>
              </span>
              <Link
                to={`/category?type=${comic.category}`}
                className="hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                {comic.category}
              </Link>
              <span className="w-3 h-3 flex items-center justify-center">
                <i className="ri-arrow-right-s-line text-xs"></i>
              </span>
              <span className="text-primary-600 truncate whitespace-nowrap">{comic.title}</span>
            </nav>
          </div>
        </div>

        {/* Hero Info */}
        <section className="bg-gradient-to-b from-background-50 to-background-100 pb-10">
          <div className="w-full px-4 md:px-6">
            <div className="flex flex-col md:flex-row gap-8 md:gap-12">
              {/* Cover */}
              <div className="flex-shrink-0 w-48 md:w-64 mx-auto md:mx-0">
                <div className="aspect-[3/4] rounded-2xl overflow-hidden bg-background-200">
                  <img
                    src={comic.coverImage}
                    alt={`${comic.title}封面`}
                    title={`${comic.title} - 尾巴漫画网好看的韩国漫画`}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>

              {/* Info */}
              <div className="flex-1 min-w-0">
                <h1 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950 mb-1">
                  {comic.title}
                </h1>
                <p className="text-foreground-500 text-sm mb-4">{comic.author} · 著</p>

                <div className="flex items-center gap-4 mb-5 flex-wrap">
                  <div className="flex items-center gap-1">
                    <span className="w-4 h-4 flex items-center justify-center text-primary-500">
                      <i className="ri-star-fill text-sm"></i>
                    </span>
                    <span className="font-heading text-xl font-bold text-foreground-950">{comic.rating}</span>
                    <span className="text-foreground-400 text-xs">/10</span>
                  </div>
                  <span className="px-3 py-1 bg-secondary-100 text-secondary-700 text-xs font-medium rounded-full whitespace-nowrap">
                    {comic.category}
                  </span>
                  <span className={`px-3 py-1 text-xs font-medium rounded-full whitespace-nowrap ${
                    comic.status === '已完结'
                      ? 'bg-accent-100 text-accent-700'
                      : 'bg-primary-100 text-primary-700'
                  }`}>
                    {comic.status}
                  </span>
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
                  <div className="bg-background-50 rounded-xl p-3 text-center border border-background-200/70">
                    <span className="font-heading text-lg font-bold text-foreground-950">{comic.chapters}</span>
                    <p className="text-foreground-500 text-xs">总话数</p>
                  </div>
                  <div className="bg-background-50 rounded-xl p-3 text-center border border-background-200/70">
                    <span className="font-heading text-lg font-bold text-foreground-950">{(comic.chapters * 0.8).toFixed(0)}万</span>
                    <p className="text-foreground-500 text-xs">人气值</p>
                  </div>
                  <div className="bg-background-50 rounded-xl p-3 text-center border border-background-200/70">
                    <span className="font-heading text-lg font-bold text-foreground-950">{comic.updateTime}</span>
                    <p className="text-foreground-500 text-xs">最近更新</p>
                  </div>
                  <div className="bg-background-50 rounded-xl p-3 text-center border border-background-200/70">
                    <span className="font-heading text-lg font-bold text-foreground-950">{(comic.rating * 8).toFixed(1)}万</span>
                    <p className="text-foreground-500 text-xs">收藏数</p>
                  </div>
                </div>

                {/* Tags */}
                {'tags' in comic && (comic as any).tags && (
                  <div className="flex items-center gap-2 mb-5 flex-wrap">
                    {(comic as any).tags.map((tag: string) => (
                      <span key={tag} className="px-3 py-1 bg-background-200/70 text-foreground-600 text-xs rounded-full whitespace-nowrap">
                        #{tag}
                      </span>
                    ))}
                  </div>
                )}

                <p className="text-foreground-700 text-sm leading-relaxed mb-6">
                  {('longDescription' in comic ? (comic as any).longDescription : comic.description)}
                </p>

                <div className="flex items-center gap-3 flex-wrap">
                  <Link
                    to={`/comic/${comic.id}/read/1`}
                    className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 text-background-50 rounded-full text-sm font-semibold whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer"
                  >
                    开始阅读
                    <span className="w-5 h-5 flex items-center justify-center">
                      <i className="ri-play-line"></i>
                    </span>
                  </Link>
                  <button className="inline-flex items-center gap-2 px-5 py-3 border border-background-200/70 text-foreground-700 rounded-full text-sm font-medium whitespace-nowrap hover:bg-background-100 transition-colors cursor-pointer">
                    <span className="w-5 h-5 flex items-center justify-center">
                      <i className="ri-bookmark-line"></i>
                    </span>
                    收藏
                  </button>
                  <button className="inline-flex items-center gap-2 px-5 py-3 border border-background-200/70 text-foreground-700 rounded-full text-sm font-medium whitespace-nowrap hover:bg-background-100 transition-colors cursor-pointer">
                    <span className="w-5 h-5 flex items-center justify-center">
                      <i className="ri-share-line"></i>
                    </span>
                    分享
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Tabs: Chapters / Comments */}
        <section className="bg-background-50 py-10 md:py-14">
          <div className="w-full px-4 md:px-6">
            <div className="flex items-center gap-1 bg-background-100 rounded-full p-1 w-fit mb-8">
              <button
                onClick={() => setActiveTab('chapters')}
                className={`px-6 py-2.5 rounded-full text-sm font-medium whitespace-nowrap transition-all duration-200 cursor-pointer ${
                  activeTab === 'chapters'
                    ? 'bg-background-50 text-foreground-950 shadow-sm'
                    : 'text-foreground-500 hover:text-foreground-700'
                }`}
              >
                章节列表 ({comic.chapters}话)
              </button>
              <button
                onClick={() => setActiveTab('comments')}
                className={`px-6 py-2.5 rounded-full text-sm font-medium whitespace-nowrap transition-all duration-200 cursor-pointer ${
                  activeTab === 'comments'
                    ? 'bg-background-50 text-foreground-950 shadow-sm'
                    : 'text-foreground-500 hover:text-foreground-700'
                }`}
              >
                读者评论 ({(comic as any).comments?.length || 0})
              </button>
            </div>

            {activeTab === 'chapters' && (
              <div>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2">
                  {'chapters_list' in comic && (comic as any).chapters_list
                    ? (comic as any).chapters_list.map((ch: { num: number; title: string; date: string }) => (
                        <Link
                          key={ch.num}
                          to={`/comic/${comic.id}/read/${ch.num}`}
                          className="flex items-center justify-between px-4 py-3 bg-background-100 hover:bg-background-200/70 rounded-xl text-sm transition-colors cursor-pointer group"
                        >
                          <span className="text-foreground-900 font-medium truncate group-hover:text-primary-600 transition-colors">
                            {ch.title}
                          </span>
                          <span className="text-foreground-400 text-xs whitespace-nowrap ml-2">{ch.date}</span>
                        </Link>
                      ))
                    : Array.from({ length: Math.min(comic.chapters, 50) }, (_, i) => (
                        <Link
                          key={i + 1}
                          to={`/comic/${comic.id}/read/${i + 1}`}
                          className="flex items-center justify-between px-4 py-3 bg-background-100 hover:bg-background-200/70 rounded-xl text-sm transition-colors cursor-pointer group"
                        >
                          <span className="text-foreground-900 font-medium group-hover:text-primary-600 transition-colors truncate">
                            第{i + 1}话
                          </span>
                        </Link>
                      ))}
                </div>
                {comic.chapters > 50 && (
                  <div className="text-center mt-8">
                    <button className="inline-flex items-center gap-2 px-6 py-2.5 border border-background-200/70 text-foreground-600 rounded-full text-sm font-medium hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap">
                      加载更多章节
                      <span className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-arrow-down-line"></i>
                      </span>
                    </button>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'comments' && (
              <div>
                {(comic as any).comments && (comic as any).comments.length > 0 ? (
                  <div className="flex flex-col gap-4">
                    {(comic as any).comments.map((comment: any) => (
                      <article key={comment.id} className="bg-background-100 rounded-2xl p-5">
                        <div className="flex items-center gap-3 mb-3">
                          <div className="w-10 h-10 rounded-full overflow-hidden bg-background-200 flex-shrink-0">
                            <img
                              src={comment.avatar}
                              alt={comment.username}
                              className="w-full h-full object-cover"
                            />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between">
                              <p className="font-label text-sm font-semibold text-foreground-900">
                                {comment.username}
                              </p>
                              <span className="text-foreground-400 text-xs">{comment.date}</span>
                            </div>
                            <div className="flex items-center gap-0.5 mt-0.5">
                              {Array.from({ length: 5 }).map((_, i) => (
                                <span key={i} className="w-3 h-3 flex items-center justify-center">
                                  <i className={`text-xs ${i < comment.rating ? 'ri-star-fill text-primary-400' : 'ri-star-line text-foreground-300'}`}></i>
                                </span>
                              ))}
                            </div>
                          </div>
                        </div>
                        <p className="text-foreground-700 text-sm leading-relaxed">{comment.content}</p>
                      </article>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-16">
                    <span className="w-12 h-12 flex items-center justify-center mx-auto mb-3 text-foreground-300">
                      <i className="ri-chat-3-line text-3xl"></i>
                    </span>
                    <p className="text-foreground-500 text-sm">暂无评论，成为第一个评论的人吧！</p>
                  </div>
                )}
              </div>
            )}
          </div>
        </section>

        {/* Related Comics */}
        <section className="bg-background-100 py-14">
          <div className="w-full px-4 md:px-6">
            <h3 className="font-heading text-2xl font-bold text-foreground-950 mb-8">相关推荐</h3>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4" data-product-shop>
              {featuredComics
                .filter((c) => c.id !== comic.id && c.category === comic.category)
                .slice(0, 6)
                .map((rel) => (
                  <article key={rel.id} className="group cursor-pointer">
                    <Link to={`/comic/${rel.id}`} className="block">
                      <div className="relative aspect-[3/4] rounded-xl overflow-hidden mb-3 bg-background-200">
                        <img
                          src={rel.coverImage}
                          alt={rel.title}
                          title={`${rel.title} - 尾巴漫画网`}
                          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                        />
                        <div className="absolute top-2 left-2">
                          <span className="inline-block px-2 py-0.5 bg-primary-500/90 backdrop-blur-sm text-background-50 text-xs font-semibold rounded-full whitespace-nowrap">
                            {rel.rating}
                          </span>
                        </div>
                      </div>
                      <h4 className="font-heading text-sm font-bold text-foreground-950 group-hover:text-primary-600 transition-colors truncate">
                        {rel.title}
                      </h4>
                      <p className="text-foreground-500 text-xs mt-0.5">{rel.author}</p>
                    </Link>
                  </article>
                ))}
            </div>
          </div>
        </section>
      </main>
      <Footer />

      {/* SEO Structured Data */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Book',
            name: comic.title,
            author: { '@type': 'Person', name: comic.author },
            description: comic.description,
            genre: comic.category,
            aggregateRating: {
              '@type': 'AggregateRating',
              ratingValue: comic.rating,
              bestRating: 10,
              ratingCount: Math.floor(comic.rating * 100),
            },
            breadcrumb: {
              '@type': 'BreadcrumbList',
              itemListElement: [
                { '@type': 'ListItem', position: 1, name: '首页', item: 'http://sansevieria.cn/' },
                { '@type': 'ListItem', position: 2, name: '分类大全', item: 'http://sansevieria.cn/category' },
                { '@type': 'ListItem', position: 3, name: comic.category, item: `http://sansevieria.cn/category?type=${comic.category}` },
                { '@type': 'ListItem', position: 4, name: comic.title },
              ],
            },
          }),
        }}
      />
    </>
  );
}